<template>
  <div
    ref="cont"
    class="f-column icmpaytbl"
    tabindex="1"
    @keydown.enter="addRow"
    @keydown.delete="removeRow(l_items.length - 1)"
  >
    <div class="icmpaytbl_row icmpaytbl_header">
      <div class="icmpaytbl_left spacer"></div>
      <div class="icmpaytbl_center">{{ $t(`solfigr.icm.stack`) }}</div>
    </div>
    <VirtualList
      ref="vl"
      style="max-height: 17.875rem"
      direction="vertical"
      :items="visibleItems"
      :itemSize
      :idKey="'position'"
      :keeps="50"
      class="large-scroll-track icmpaytbl_body flex-1"
    >
      <template #item="{ item, index }">
        <slot
          :item="item"
          :index="index"
          :size="itemSize"
        >
          <div
            class="icmpaytbl_row gw_hvr"
            :class="{ 'no-events': hasIpOopStacks && index < 2 }"
            :data-tst="`remaining_stack_${item.position || index}`"
          >
            <div
              class="icmpaytbl_left truncate"
              :title="index + 1"
              :data-tst="`remaining_stack_position_${item.position || index}`"
            >
              {{ item.display_position || index + 1 }}
            </div>
            <div class="icmpaytbl_center">
              <NumberInput
                :min="conf.remaining_stack.min"
                :max="conf.remaining_stack.max"
                :step="conf.remaining_stack.step"
                :decimals="conf.remaining_stack.decimals"
                :inputmode="conf.remaining_stack.inputmode"
                :hasMinus="false"
                :hasPlus="false"
                :hasWheelScroll="false"
                style="--input-width: 100%"
                :value="item.stack"
                :focusOnMounted="index === visibleItems.length - 1"
                :style="
                  hasIpOopStacks && index < 2
                    ? `pointer-events: none; --input-color: var(--clr-grey-2);            `
                    : ``
                "
                @input="
                  value => {
                    if (hasIpOopStacks && index < 2) {
                      return
                    }
                    handleInput(value, hasIpOopStacks ? index - 2 : index)
                  }
                "
              />
            </div>

            <div
              v-if="hasIpOopStacks && index > 1 && items.length > min"
              class="icmpaytbl_close icon_btn mdi pa-1 mdi-close-circle csst gw_hvr_content"
              style="--clr-hover: var(--clr-error)"
              data-tst="remove_remaining_stack"
              @click="removeRow(hasIpOopStacks ? index - 2 : index, false)"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
            >
              <div
                class="csst_content clr-white"
                style="position: fixed"
              >
                {{ $t('common.remove') }}
                <span class="keyboardkey_key"> Delete </span>
              </div>
            </div>
          </div>
        </slot>
      </template>
    </VirtualList>
    <div
      class="icmpaytbl_row icmpaytbl_footer"
      style="gap: 0.5rem"
    >
      <div
        v-if="items.length < max"
        class="gw_btn clr-grey clr-hover_white weight_500"
        data-tst="add_remaining_stack"
        @click="addRow"
      >
        <i class="mdi mdi-plus mr-1"></i>{{ $t(`solfigr.icm.add_row`) }}
      </div>

      <div
        v-if="!canClearAll"
        class="gw_btn clr-grey clr-hover_white weight_500"
        data-tst="clear_all"
        @click="canClearAll = true"
      >
        <i class="mdi mdi-trash-can mr-1"></i>{{ $t(`common.clear_all`) }}
      </div>
      <div
        v-if="canClearAll"
        class="gw_btn clr-grey clr-hover_white weight_500"
        data-tst="cancel_clear_all"
        @click="canClearAll = false"
      >
        <i class="mdi mdi-close"></i>
      </div>
      <div
        v-if="canClearAll"
        class="gw_btn weight_500 anim-fade-in"
        style="--clr-back: var(--clr-error)"
        data-tst="confirm_clear_all"
        @click="
          () => {
            clearAll()
            canClearAll = false
          }
        "
      >
        <i class="mdi mdi-trash-can mr-1"></i>
        {{ $t('btn.confirm') }}
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import NumberInput from '@/components/lib/NumberInput'
import { getSolfigrFields } from '@/store/solfigr/solfigr_helpers'
import VirtualList from '@/components/vue3/VirtualList/VirtualList'
import format_mix from '@/mixins/format_mix'
import float_mix from '@/mixins/float_mix'

export default {
  name: 'SolfigrIcmRemainingStacksTable',
  components: {
    VirtualList,
    NumberInput,
  },
  mixins: [format_mix, float_mix],
  props: {
    loading: Boolean,
    items: Object,
    max: Number,
    min: Number,
    stackOOP: Number,
    stackIP: Number,
    playerIP: Object,
    playerOOP: Object,
  },
  emits: ['update:items', 'remove:item'],
  data() {
    this.conf = getSolfigrFields()
    return {
      canClearAll: false,
    }
  },
  methods: {
    addRow() {
      if (this.items.length >= this.max) return

      let res = [...this.items]
      res.push(this.items[this.items.length - 1] || this.conf.remaining_stack.min)
      this.update(res)
      this.scrollToLast(res)
    },
    removeRow(index, scrollToLast) {
      const res = [...this.items].filter((_item, idx) => idx !== index)
      this.update(res)

      if (!scrollToLast) return
      this.scrollToLast(res)
    },
    clearAll() {
      this.update([...Array(this.min || 0).fill(this.conf.remaining_stack.min)])
    },
    handleInput(value, index) {
      let res = [...this.items]
      res[index] = value
      this.update(res)
    },
    remove() {
      if (this.items.length === 2) return

      this.$emit('remove:item')
    },
    update(value) {
      this.$emit('update:items', value)
    },
    scrollToLast(items) {
      nextTick(() => {
        this.$refs.vl.scrollToIndex(items.length - 1)
      })
    },
  },
  computed: {
    l_items() {
      return this.items.map((item, index) => ({
        stack: item,
        position: index + 1,
      }))
    },
    visibleItems() {
      if (this.hasIpOopStacks) {
        return [
          ...[
            {
              stack: this.stackOOP,
              display_position: this.playerOOP.display_position,
              position: 'OOP',
            },
            {
              stack: this.stackIP,
              display_position: this.playerIP.display_position,
              position: 'IP',
            },
          ],
          ...this.l_items,
        ]
      }
      return this.l_items
    },
    hasIpOopStacks() {
      return !!(this.stackIP && this.stackOOP)
    },
    globals_window_size() {
      return this.$store.getters.globals_window_size
    },
    itemSize() {
      if (this.globals_window_size) {
        const fntSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize)
        return fntSizePx * 2.5
      }
      return 35
    },
  },
}
</script>
<style lang="scss" scoped>
.icmpaytbl {
  border-radius: 4px;
  color: var(--clr-grey-2);
  &_row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    height: 2.5rem;
    flex-shrink: 0;
    transition: 0.22s;
    padding: 0 0.5rem;
    gap: 0.5rem;
  }
  &_header {
    background: var(--clr-black-1-2);
    height: 3rem;
    border-radius: 4px 4px 0 0;
  }
  &_footer {
    background: var(--clr-black-1-2);
    border-radius: 0 0 4px 4px;
    height: 3rem;
    padding: 0.5rem;
  }
  &_body {
    background: var(--clr-black-1);
    border-left: 2px solid var(--clr-black-1-2);
    border-right: 2px solid var(--clr-black-1-2);
  }
  &_left {
    width: 3rem;
    overflow: hidden;
  }
  &_center {
    width: 7rem;
  }
  &_right {
    width: 7rem;
  }
}

@media (hover: hover) and (pointer: fine) {
  .icmpaytbl_row {
    &:hover {
      background: rgba(255, 255, 255, 0.05);
    }
  }
}
</style>
